<template>
	<v-card class="forum-preview-card elevation-0 d-flex flex-column pt-2 mb-1">
		<div class="forum-preview-card__container">
			<div class="forum-preview-card__outer">
				<div class="d-flex forum-preview-card__list pl-0 pr-0 pt-3">
					<router-link
						:to="{
							name: isCurrentUser(this.forum.user.user_id) ? 'profile' : 'user-by-id',
							params: isCurrentUser(this.forum.user.user_id)
								? []
								: { id: this.forum.user.user_id },
						}"
						class="text-decoration-none mb-1"
						:class="{ disabled: this.forum.user.user_hidden }"
					>
						<div class="forum-preview-card__header px-3">
							<div class="d-flex align-center">
								<Avatar
									class="mr-2 category-card__avatar"
									:size="52"
									:avatar="forum.user.user_logo"
								/>
								<div class="forum-preview-card__meta">
									<span class="forum-preview-card__meta-top">
										{{ forum.user.visible_username }}
									</span>
									<span class="forum-preview-card__meta-bottom">{{
										forum.user.second_profile_row
									}}</span>
								</div>
							</div>
							<div class="forum-preview-card__meta-time">
								{{ calcDateAgo(forum.created) }}
							</div>
						</div>
					</router-link>

					<router-link
						:to="{ name: 'forum-by-id', params: { id: forum.forum_id } }"
						style="text-decoration: none"
					>
						<div
							class="forum-card__forum-text pb-0 pt-3 px-3"
							v-html="postDescription(forum.forum_text)"
						></div>
					</router-link>

					<div class="d-flex align-center" v-if="forum.uploads && forum.uploads.length">
						<router-link
							class="d-flex align-center px-3 mt-3 text-decoration-none"
							:to="{ name: 'forum-file-by-id', params: { id: forum.forum_id } }"
						>
							<div
								class="d-flex flex-row flex-wrap w-full"
								v-for="attachment in this.forum.uploads.slice(0, 6)"
								:key="attachment.upload_id"
							>
								<PostAttachments
									backgroundColor="#F3F3F6"
									:attachment="attachment"
								/>
							</div>
							<div
								v-if="forum.uploads.length > 6"
								class="more-attachments"
								style="cursor: pointer"
							>
								+{{ countAttachments }}
							</div>
						</router-link>
					</div>

					<v-divider class="mt-3" />

					<div class="d-flex justify-space-between align-center px-3 py-3">
						<div class="d-flex">
							<div class="d-flex align-center mr-6">
								<Icon type="eye" color="#9b9b9b" :size="24" :pr="6" />
								<div class="forum-preview-card__counter-num">
									{{ forum.views_count }}
								</div>
							</div>
							<div @click="toggleToForum" class="d-flex align-center cursor-pointer">
								<Icon type="comment" color="#9b9b9b" :size="24" :pr="6" />
								<div class="forum-preview-card__counter-num">
									{{ forum.comment_count }}
								</div>
							</div>
						</div>
						<div @click="toggleReplyForumList" class="forum-preview-card__reply">
							<router-link
								:to="{
									name: 'forum-by-id',
									params: { id: forum.forum_id },
									query: { toBottom: true },
								}"
							>
								Reply
							</router-link>
						</div>
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { mapGetters } from 'vuex'
import Avatar from '@/components/Avatar'
import moment from 'moment'
import SessionAttachments from '@/components/Resource/SessionAttachments.vue'
import Dwh from '@/mixins/Dwh'
import PostAttachments from '@/components/Post/PostAttachments.vue'
import linkifyThreadText from '@/mixins/linkifyThreadText'

Vue.use(VueScrollTo)

export default {
	name: 'ForumPreview',
	mixins: [Dwh, linkifyThreadText],
	components: {
		PostAttachments,
		Avatar,
		SessionAttachments,
	},
	props: {
		forum: {
			type: Object,
			default: () => ({}),
		},
	},
	methods: {
		toggleToForum() {
			this.$router.push({
				name: 'forum-by-id',
				params: { id: this.forum.forum_id },
			})
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
		toggleReplyForumList() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_reply_forum_post_list',
				module: 'Forums',
				chapter_id: this.forum.forum_id,
			})
		},
		calcDateAgo(date) {
			return moment(date * 1000).fromNow()
		},
	},
	computed: {
		...mapGetters('auth', ['isAuthenticated']),
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		countAttachments() {
			if (Array.isArray(this.forum.uploads)) {
				return this.forum.uploads.length - 6
			}
			return 0
		},
	},
}
</script>

<style lang="scss">
.forum-preview-card {
	background: #f1f1f1 !important;

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 8px !important;
		background: #ffffff !important;
		border: 1px solid #e2e2e2 !important;
	}

	&__counter {
		&-num {
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: #9b9b9b;
		}
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;

		@media (max-width: 480px) {
			flex-direction: column;
		}
	}

	&__reply {
		display: flex;
		justify-content: flex-end;
		color: var(--accent-color);
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		text-decoration: unset !important;
		cursor: pointer;

		a {
			color: var(--accent-color) !important;
			text-decoration: unset !important;
		}
	}

	&__meta {
		display: flex;
		justify-content: space-between;
		flex-direction: column;
		word-break: break-word;

		&-bottom {
			color: var(--company-position-large);
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 15px;
			line-height: 20px;
			letter-spacing: 0;
		}
		&-top {
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--username-category-title);
		}

		&-time {
			height: 46px;
			display: flex;
			align-items: flex-end;
			color: #9b9b9b;
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: right;

			@media (max-width: 480px) {
				text-align: left;
				padding-left: 58px;
			}
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
	}
	&__title {
		color: #4a4a4a !important;
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 16px;
		line-height: 100%;
		letter-spacing: 0;
		word-break: break-word;
		white-space: pre-wrap;
		a {
			color: var(--link) !important;
			text-decoration: underline;
			cursor: pointer;

			&:hover {
				text-decoration: none;
			}
		}
	}

	&__content {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 16px;
		line-height: 21px;
	}

	&__thumbnail {
		height: auto;
		width: 100%;
	}

	&__menu-dots {
		margin-left: 10px !important;
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
		}
		&__default-thumbnail {
			max-height: 250px;
			height: 100%;
		}
		&__outer {
			flex-direction: column;
		}
	}
}
.v-application p {
	margin-bottom: 0;
}
</style>
